import React from 'react';
import { Card } from '@/components/ui/card';
import * as TWEEN from '@tweenjs/tween.js';
import { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, Activity, Target, BarChart3, Calendar, Award } from 'lucide-react';
import { InfoTooltip } from '@/components/ui/info-tooltip';

// Infobulles pour les métriques de backtest
const METRIC_TOOLTIPS = {
  totalReturn: "Gain ou perte total en pourcentage depuis le capital initial sur toute la période du backtest.",
  annualizedReturn: "Taux de croissance annuel composé (CAGR). Rendement annualisé moyen sur la période.",
  maxDrawdown: "Perte maximale subie entre un pic et un creux. Exemple : -20% signifie que le portefeuille a perdu jusqu'à 20% depuis son plus haut.",
  sharpeRatio: "Mesure du rendement ajusté au risque. Au-dessus de 1 = bon. Au-dessus de 2 = excellent. Négatif = mauvais.",
  volatility: "Amplitude des variations de valeur. Plus c'est élevé, plus le portefeuille fluctue. Exprimé en % annualisé.",
  winRate: "Pourcentage de mois avec un rendement positif. Au-dessus de 60% = excellent.",
  bestMonth: "Meilleure performance mensuelle sur la période du backtest.",
  worstMonth: "Pire performance mensuelle sur la période du backtest.",
};

const AnimatedNumber = ({ value, duration = 1000, decimals = 2, prefix = '', suffix = '' }) => {
  // Handle undefined/null/NaN values
  const safeValue = (value === undefined || value === null || isNaN(value)) ? 0 : Number(value);
  
  const [displayValue, setDisplayValue] = useState(safeValue);
  const requestRef = useRef();
  const tweenRef = useRef();

  useEffect(() => {
    // Stop any existing animation
    if (tweenRef.current) {
      tweenRef.current.stop();
    }
    if (requestRef.current) {
      cancelAnimationFrame(requestRef.current);
    }

    // Start from current display value
    const obj = { value: displayValue };
    tweenRef.current = new TWEEN.Tween(obj)
      .to({ value: safeValue }, duration)
      .easing(TWEEN.Easing.Quadratic.Out)
      .onUpdate(() => {
        setDisplayValue(obj.value);
      })
      .onComplete(() => {
        setDisplayValue(safeValue); // Ensure final value is exact
      })
      .start();

    const animate = (time) => {
      requestRef.current = requestAnimationFrame(animate);
      TWEEN.update(time);
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      if (tweenRef.current) {
        tweenRef.current.stop();
      }
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [safeValue, duration]);

  return (
    <span>
      {prefix}
      {displayValue.toFixed(decimals)}
      {suffix}
    </span>
  );
};

const MetricCard = ({ title, value, icon: Icon, color, decimals = 2, prefix = '', suffix = '', tooltip = '' }) => {
  
  const colorClasses = {
    green: 'bg-slate-800/80 border-emerald-500/30',
    red: 'bg-slate-800/80 border-red-500/30',
    blue: 'bg-slate-800/80 border-blue-500/30',
    amber: 'bg-slate-800/80 border-amber-500/30',
    purple: 'bg-slate-800/80 border-purple-500/30',
  };

  const iconColorClasses = {
    green: 'text-emerald-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
    purple: 'text-purple-400',
  };

  const textColorClasses = {
    green: 'text-emerald-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
    purple: 'text-purple-400',
  };

  return (
    <Card className={`p-4 border-2 transition-all duration-300 hover:shadow-lg ${colorClasses[color]}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <p className="text-xs font-medium text-white">{title}</p>
            {tooltip && <InfoTooltip content={tooltip} />}
          </div>
          <h3 className={`text-2xl font-bold ${textColorClasses[color]}`}>
            <AnimatedNumber value={value} decimals={decimals} prefix={prefix} suffix={suffix} />
          </h3>
        </div>
        <div className={`p-2 rounded-full bg-slate-700/50 ${iconColorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Card>
  );
};

const BacktestMetrics = ({ metrics }) => {
  if (!metrics) return null;

  const {
    totalReturn,
    annualizedReturn,
    maxDrawdown,
    sharpeRatio,
    volatility,
    winRate,
    bestMonth,
    worstMonth
  } = metrics;

  return (
    <div className="space-y-6" data-testid="backtest-metrics">
      <h3 className="text-xl font-bold text-white">Performance Metrics</h3>
      
      {/* Primary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Return"
          value={totalReturn}
          icon={totalReturn >= 0 ? TrendingUp : TrendingDown}
          color={totalReturn >= 0 ? 'green' : 'red'}
          prefix={totalReturn >= 0 ? '+' : ''}
          suffix="%"
          decimals={2}
          tooltip={METRIC_TOOLTIPS.totalReturn}
        />
        <MetricCard
          title="Annualized Return"
          value={annualizedReturn}
          icon={annualizedReturn >= 0 ? TrendingUp : TrendingDown}
          color={annualizedReturn >= 0 ? 'green' : 'red'}
          prefix={annualizedReturn >= 0 ? '+' : ''}
          suffix="%"
          decimals={2}
          tooltip={METRIC_TOOLTIPS.annualizedReturn}
        />
        <MetricCard
          title="Max Drawdown"
          value={Math.abs(maxDrawdown)}
          icon={Activity}
          color={Math.abs(maxDrawdown) < 15 ? 'green' : Math.abs(maxDrawdown) < 25 ? 'amber' : 'red'}
          prefix="-"
          suffix="%"
          decimals={2}
          tooltip={METRIC_TOOLTIPS.maxDrawdown}
        />
        <MetricCard
          title="Sharpe Ratio"
          value={sharpeRatio}
          icon={Target}
          color={sharpeRatio >= 1 ? 'green' : sharpeRatio >= 0.5 ? 'amber' : 'red'}
          decimals={3}
          tooltip={METRIC_TOOLTIPS.sharpeRatio}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Volatility"
          value={volatility}
          icon={Activity}
          color="blue"
          suffix="%"
          decimals={2}
          tooltip={METRIC_TOOLTIPS.volatility}
        />
        <MetricCard
          title="Win Rate"
          value={winRate}
          icon={Award}
          color={winRate >= 60 ? 'green' : winRate >= 50 ? 'amber' : 'red'}
          suffix="%"
          decimals={1}
          tooltip={METRIC_TOOLTIPS.winRate}
        />
        <MetricCard
          title="Best Month"
          value={bestMonth}
          icon={TrendingUp}
          color="green"
          prefix="+"
          suffix="%"
          decimals={2}
          tooltip={METRIC_TOOLTIPS.bestMonth}
        />
        <MetricCard
          title="Worst Month"
          value={Math.abs(worstMonth)}
          icon={TrendingDown}
          color="red"
          prefix="-"
          suffix="%"
          decimals={2}
          tooltip={METRIC_TOOLTIPS.worstMonth}
        />
      </div>
    </div>
  );
};

export default BacktestMetrics;
